import type { ReactNode } from 'react';
import { plazoAutoconfirmar } from '../nucleo/fechas';
import type { Partido } from '../services/api';

/**
 * Un partido como se lee de un vistazo: las dos parejas y el marcador.
 *
 * El equipo del que mira va resaltado — en un americano jugás con distinta
 * gente cada vez, y lo primero que buscás en la lista es dónde estabas vos.
 */
export function MarcadorPartido({
  partido,
  yoId,
  accion,
  onAbrir,
}: {
  partido: Partido;
  yoId: number;
  /** Un boton chico (Anular, por ejemplo) a la derecha del renglon de la fecha. */
  accion?: ReactNode;
  /** Tocar las parejas abre el detalle: quien lo cargo, a que hora, quien lo confirmo. */
  onAbrir?: () => void;
}) {
  const equipoA = partido.jugadores.filter((j) => j.equipo === 'A');
  const equipoB = partido.jugadores.filter((j) => j.equipo === 'B');

  const ganoA = partido.games_a > partido.games_b;
  const miEquipo = partido.jugadores.find((j) => j.id === yoId)?.equipo;

  const tachado = partido.estado === 'anulado' || partido.estado === 'rechazado';
  const parejas = (
    <div className={tachado ? 'line-through opacity-60' : ''}>
      <Lado jugadores={equipoA} games={partido.games_a} gano={ganoA} esMio={miEquipo === 'A'} />
      <Lado jugadores={equipoB} games={partido.games_b} gano={!ganoA} esMio={miEquipo === 'B'} />
    </div>
  );

  return (
    <div>
      {/* Fecha y estado juntos a la izquierda; la derecha queda para la
          accion, asi la tarjeta no necesita un renglon aparte para el boton. */}
      <div className="mb-1 flex min-h-8 items-center justify-between gap-2 text-xs text-(--color-apagado)">
        <span className="flex flex-wrap items-center gap-x-1.5">
          <span>{fechaCorta(partido.jugado_el)}</span>
          {partido.tb_a !== null && partido.tb_b !== null && (
            <span>
              · tiebreak {partido.tb_a}-{partido.tb_b}
            </span>
          )}
          {partido.estado === 'anulado' && (
            <span className="text-(--color-error)">
              · {partido.motivo_anulacion === 'duplicado' ? 'anulado por duplicado' : 'anulado'}
            </span>
          )}
          {partido.estado === 'rechazado' && (
            <span className="text-(--color-error)">· rechazado</span>
          )}
          {partido.estado === 'pendiente' && (
            <span className="text-(--color-alerta)">· sin confirmar</span>
          )}
          {/* Un partido que no confirmo un rival se marca: el que nadie
            confirmo a tiempo y el que confirmo un admin. Que se vea es la
            mitad del trato: si contara igual pero en silencio, el registro
            estaria mintiendo sobre lo que paso. */}
          {partido.estado === 'confirmado' && partido.confirmado_por === null && (
            <span
              title={`Nadie lo confirmó a tiempo (en ${plazoAutoconfirmar()}, o antes de que cerrara el mes), así que el sistema lo dio por bueno`}
            >
              · confirmado por inactividad
            </span>
          )}
          {partido.estado === 'confirmado' && loConfirmoUnAdmin(partido) && (
            <span title="Ningún rival lo confirmó: lo confirmó un administrador">
              · confirmado por admin
            </span>
          )}
        </span>
        {accion && <span className="shrink-0">{accion}</span>}
      </div>

      {/* El boton es solo el bloque de las parejas, no la tarjeta entera: la
          accion del renglon de arriba es otro boton, y uno adentro de otro no
          se puede. */}
      {onAbrir ? (
        <button
          onClick={onAbrir}
          aria-label="Ver quién lo cargó y cuándo"
          className="flex w-full items-center gap-1 text-left transition active:scale-[0.99]"
        >
          <span className="min-w-0 flex-1">{parejas}</span>
          <span className="shrink-0 text-lg text-(--color-apagado)" aria-hidden>
            ›
          </span>
        </button>
      ) : (
        parejas
      )}

      {partido.nota && <p className="mt-2 text-xs text-(--color-apagado) italic">{partido.nota}</p>}
    </div>
  );
}

function Lado({
  jugadores,
  games,
  gano,
  esMio,
}: {
  jugadores: Partido['jugadores'];
  games: number;
  gano: boolean;
  esMio: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-lg px-2 py-1
        ${esMio ? 'bg-white/5' : ''}`}
    >
      <span className={`flex-1 text-sm ${gano ? 'font-semibold' : 'text-(--color-apagado)'}`}>
        {jugadores.map((j) => j.nombre).join('  ·  ')}
      </span>
      <span
        className={`w-7 text-right text-base tabular-nums
          ${gano ? 'font-bold text-(--color-acento)' : 'text-(--color-apagado)'}`}
      >
        {games}
      </span>
    </div>
  );
}

function fechaCorta(fecha: string): string {
  const [, mes, dia] = fecha.split('-') as [string, string, string];
  return `${Number(dia)}/${Number(mes)}`;
}

/** Lo confirmo alguien que no jugo en el equipo contrario al del que lo cargo. */
function loConfirmoUnAdmin(partido: Partido): boolean {
  if (partido.confirmado_por === null) return false;
  const equipoDe = (id: number) => partido.jugadores.find((j) => j.id === id)?.equipo;
  const suyo = equipoDe(partido.confirmado_por);
  return suyo === undefined || suyo === equipoDe(partido.cargado_por);
}
